import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AppButton, Box, ButtonResponsiveWrapper, Preloader, useResponsive } from '@common-fe/common-fe';
import _ from 'lodash';

import { WarnModal } from '@/components/elements';
import { SnackbarState } from '@/modules/core/core.types';
import { useSnackbar } from '@/modules/core/hooks';
import { LettersOfMedicalNecessity } from '@/modules/MyDocuments/myDocuments.types';
import useDeleteLettersOfMedicalNecessityQuery from '@/modules/MyDocuments/queries/useDeleteLettersOfMedicalNecessity.query';

const CANNOT_DELETE_MESSAGE = 'can not be deleted because it has claims';

interface Props {
  deletedItem?: LettersOfMedicalNecessity;
  onSetId: (id: string) => void;
}
const LettersOfMedicalNecessityDeleteModal: React.FC<Props> = ({ deletedItem, onSetId }) => {
  const { isMobile } = useResponsive();
  const { t } = useTranslation();
  const { handleDelete } = useDeleteLettersOfMedicalNecessityQuery();
  const [loading, setLoading] = useState(false);
  const { handleAddPermanentSnackbar } = useSnackbar();
  const [errorModalVisible, setErrorModalVisible] = useState(false);

  const handleClose = useCallback(() => {
    onSetId('');
  }, [onSetId]);

  const handleConfirm = useCallback(async () => {
    setLoading(true);
    try {
      await handleDelete(deletedItem?.id || '');
      onSetId('');
      handleAddPermanentSnackbar({
        text: `${deletedItem?.name} has been deleted.`,
        state: SnackbarState.negative,
        closeIcon: true,
      });
    } catch (e) {
      const errorMessage = _.get(e, 'response.data.elevate_error_message', '') as string;
      const isCannotDelete = errorMessage?.includes(CANNOT_DELETE_MESSAGE);

      if (isCannotDelete) {
        setErrorModalVisible(true);
      }
    } finally {
      setLoading(false);
    }
  }, [handleDelete, deletedItem, onSetId, handleAddPermanentSnackbar]);

  if (!deletedItem) return null;

  return (
    <>
      <WarnModal
        testId="letter-of-medical-necessity-delete"
        visible={!!deletedItem && !errorModalVisible}
        header="Delete this Letter of Medical Necessity?"
        onSetVisible={handleClose}
        cancelButtonNode={
          <ButtonResponsiveWrapper fill={isMobile}>
            <AppButton
              fill={isMobile}
              testId="letter-of-medical-necessity-delete_cancel"
              buttonType="secondary"
              onClick={handleClose}
              width="150px"
              disabled={loading}
            >
              {t('Cancel')}
            </AppButton>

            <AppButton
              fill={isMobile}
              testId="letter-of-medical-necessity-delete_apply"
              color="red"
              onClick={handleConfirm}
              width="150px"
              disabled={loading}
            >
              {loading ? <Preloader color="white" /> : t('Yes, Delete')}
            </AppButton>
          </ButtonResponsiveWrapper>
        }
      />

      {/* Error Modal */}
      <WarnModal
        visible={errorModalVisible}
        header="Can't Delete LMN"
        helptext="You can’t delete this LMN because it is associated with an approved claim."
        onSetVisible={() => {
          setErrorModalVisible(false);
          handleClose();
        }}
        cancelButtonNode={
          <Box fill={isMobile} margin={{ left: 'auto', right: 'auto' }}>
            <AppButton
              fill={isMobile}
              buttonType="primary"
              onClick={() => {
                setErrorModalVisible(false);
                handleClose();
              }}
              width="150px"
            >
              {t('Close')}
            </AppButton>
          </Box>
        }
      />
    </>
  );
};

export default LettersOfMedicalNecessityDeleteModal;
